/**
 * T8013 · Online/offline navigator binding → connectivity state machine.
 *
 * SPEC ANCHOR: `specs/009-data-fetching-audit/05-new-infra-sdk-changes.html`
 * §L1453 — `navigator.online`/`offline` events drive the REST-healthy
 * signal. `offline` flips immediately; `online` runs a probe FIRST
 * (captive networks lie) and only then flips healthy + triggers the
 * bulk-snapshot reconnect ladder.
 */

export interface NetInfoAdapter {
  subscribe(handler: (online: boolean) => void): () => void;
}

export interface OnlineOfflineDeps {
  readonly netInfo: NetInfoAdapter;
  readonly setRestHealthy: (healthy: boolean, cause: string) => void;
  readonly runReconnectLadder: () => Promise<void>;
  readonly probeRest: () => Promise<boolean>;
}

/**
 * Install the handler. Returns the adapter's unsubscribe function so the
 * caller can tear down on registry HMR replace / app shutdown.
 */
export function installOnlineOfflineHandler(deps: OnlineOfflineDeps): () => void {
  return deps.netInfo.subscribe(async (online: boolean): Promise<void> => {
    if (!online) {
      deps.setRestHealthy(false, 'navigator-offline');
      return;
    }
    const ok = await deps.probeRest().catch((): boolean => false);
    if (ok) {
      deps.setRestHealthy(true, 'navigator-online-verified');
      await deps.runReconnectLadder().catch((): void => {});
    } else {
      deps.setRestHealthy(false, 'captive-network-suspected');
    }
  });
}

// `createWebNetInfoAdapter` moved to `src/platform/web/net-info-adapter.ts`
// per `.claude/rules/platform-agnostic.md`. The `installOnlineOfflineHandler`
// above stays platform-neutral — it consumes `NetInfoAdapter` via DI.
